import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Building2,
  Check,
  FileSignature,
  Link2,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  TriangleAlert,
  Unlink,
} from 'lucide-react';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { EmptyState } from '../components/ui/empty-state';
import { Input } from '../components/ui/input';
import { LoadingState } from '../components/ui/loading-state';
import { Modal } from '../components/ui/modal';
import { PageHeader } from '../components/ui/page-header';
import { cn } from '../lib/utils';
import {
  CreateEplCompanyRequest,
  EdoOperatorDto,
  EplCompanyDto,
  EplTenantDto,
} from '../models';
import {
  createEplCompany,
  getApiError,
  getEdoOperators,
  getEplIntegrations,
  updateEplCompanyOperator,
} from '../services/api';

type OperatorFilter = 'all' | 'unassigned' | number;

const operatorDisplayName = (operator?: EdoOperatorDto, fallback?: string) => {
  const name = operator?.name || fallback || '';
  if (/kontur/i.test(name)) return 'Контур / Диадок';
  if (/astral/i.test(name)) return 'Калуга Астрал';
  if (/tensor|sbis|saby/i.test(name)) return 'СБИС / Saby';
  return name || 'Оператор не указан';
};

export const EplIntegrationsPage = () => {
  const [items, setItems] = useState<EplTenantDto[]>();
  const [operators, setOperators] = useState<EdoOperatorDto[]>();
  const [selectedId, setSelectedId] = useState<number>();
  const [filter, setFilter] = useState<OperatorFilter>('all');
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createInn, setCreateInn] = useState('');
  const [createOperatorId, setCreateOperatorId] = useState<number>();
  const [editingCompany, setEditingCompany] = useState<EplCompanyDto>();
  const [operatorDraft, setOperatorDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async (preferredTenantId?: number) => {
    setError('');
    try {
      const [tenantResponse, operatorResponse] = await Promise.all([
        getEplIntegrations(),
        getEdoOperators(),
      ]);
      const tenants = tenantResponse.data.data;
      const availableOperators = operatorResponse.data.data;
      setItems(tenants);
      setOperators(availableOperators);
      setSelectedId((current) => preferredTenantId || current || tenants[0]?.tenantId);
      setCreateOperatorId((current) => current || availableOperators[0]?.id);
    } catch (requestError) {
      setError(getApiError(requestError));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const selected = useMemo(
    () => items?.find((item) => item.tenantId === selectedId),
    [items, selectedId],
  );
  const companies = useMemo(() => selected?.companies || [], [selected]);
  const connectedCount = companies.filter((company) => company.edoOperatorId).length;
  const filteredCompanies = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return companies.filter((company) => {
      const matchesFilter = filter === 'all'
        || (filter === 'unassigned' && !company.edoOperatorId)
        || (typeof filter === 'number' && Number(company.edoOperatorId) === filter);
      const matchesQuery = !normalizedQuery
        || company.name.toLowerCase().includes(normalizedQuery)
        || String(company.inn || '').includes(normalizedQuery);
      return matchesFilter && matchesQuery;
    });
  }, [companies, filter, query]);

  const resetCreate = () => {
    setCreateOpen(false);
    setCreateName('');
    setCreateInn('');
    setCreateOperatorId(operators?.[0]?.id);
  };

  const handleCreate = async () => {
    if (!selected || !createOperatorId) return;
    const request: CreateEplCompanyRequest = {
      tenantId: selected.tenantId,
      name: createName.trim(),
      inn: createInn.trim(),
      edoOperatorId: createOperatorId,
    };
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await createEplCompany(request);
      setSuccess(`Организация «${request.name}» добавлена и подключена к ${operatorDisplayName(operators?.find((operator) => operator.id === request.edoOperatorId))}.`);
      resetCreate();
      await load(selected.tenantId);
    } catch (requestError) {
      setError(getApiError(requestError));
    } finally {
      setSubmitting(false);
    }
  };

  const openOperatorEditor = (company: EplCompanyDto) => {
    setEditingCompany(company);
    setOperatorDraft(company.edoOperatorId ? String(company.edoOperatorId) : '');
  };

  const handleOperatorUpdate = async () => {
    if (!editingCompany || !selected) return;
    const edoOperatorId = operatorDraft ? Number(operatorDraft) : null;
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await updateEplCompanyOperator(editingCompany.id, edoOperatorId);
      setSuccess(edoOperatorId
        ? `Для «${editingCompany.name}» выбран ${operatorDisplayName(operators?.find((operator) => operator.id === edoOperatorId))}.`
        : `«${editingCompany.name}» отключена от ЭПЛ. Компания и история сохранены.`);
      setEditingCompany(undefined);
      await load(selected.tenantId);
    } catch (requestError) {
      setError(getApiError(requestError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppLayout title="ЭПЛ и операторы ЭДО">
      <PageHeader
        title="Организации в ЭПЛ"
        description="Подключайте клиентов к Контур/Диадок, Калуга Астрал и СБИС/Saby без ручной правки базы."
        action={(
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => load(selectedId)}><RefreshCw className="h-4 w-4" /> Обновить</Button>
            <Button onClick={() => setCreateOpen(true)} disabled={!selected || !operators?.length}><Plus className="h-4 w-4" /> Добавить организацию</Button>
          </div>
        )}
      />

      {error && <Alert tone="error" icon={TriangleAlert}>{error}</Alert>}
      {success && <Alert tone="success" icon={Check}>{success}</Alert>}

      {!items || !operators ? <LoadingState /> : items.length === 0 ? (
        <EmptyState icon={<Building2 className="h-8 w-8" />} title="Нет медицинских организаций" description="Сначала создайте медицинскую организацию в разделе «Организации»." />
      ) : (
        <>
          <section className="mb-5 rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-5">
            <div className="grid gap-4 lg:grid-cols-[minmax(240px,360px)_1fr] lg:items-end">
              <div className="space-y-1.5">
                <label htmlFor="epl-tenant" className="text-sm font-semibold text-foreground">Медицинская организация</label>
                <select id="epl-tenant" value={selectedId || ''} onChange={(event) => { setSelectedId(Number(event.target.value)); setFilter('all'); setQuery(''); }} className="h-11 w-full cursor-pointer rounded-lg border border-input bg-background px-3 text-sm font-medium text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20">
                  {items.map((item) => <option key={item.tenantId} value={item.tenantId}>{item.tenantName}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <Metric label="Всего клиентов" value={companies.length} icon={Building2} tone="primary" />
                <Metric label="Подключено" value={connectedCount} icon={Link2} tone="success" />
                <Metric label="Без оператора" value={companies.length - connectedCount} icon={Unlink} tone="neutral" className="col-span-2 sm:col-span-1" />
              </div>
            </div>
          </section>

          <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            <div className="border-b border-border p-4 sm:p-5">
              <div className="flex flex-wrap gap-2" role="group" aria-label="Фильтр по оператору ЭДО">
                <FilterButton active={filter === 'all'} onClick={() => setFilter('all')} label="Все" count={companies.length} />
                {operators.map((operator) => (
                  <FilterButton key={operator.id} active={filter === operator.id} onClick={() => setFilter(operator.id)} label={operatorDisplayName(operator)} count={companies.filter((company) => Number(company.edoOperatorId) === operator.id).length} />
                ))}
                <FilterButton active={filter === 'unassigned'} onClick={() => setFilter('unassigned')} label="Не подключены" count={companies.filter((company) => !company.edoOperatorId).length} />
              </div>
              <div className="mt-4 flex w-full max-w-md items-center gap-2 rounded-lg border border-border bg-background px-3 py-2.5">
                <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Название или ИНН" className="w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground" />
              </div>
            </div>

            {filteredCompanies.length === 0 ? (
              <EmptyState icon={<FileSignature className="h-8 w-8" />} title="Организации не найдены" description={companies.length ? 'Измените фильтр или поисковый запрос.' : 'Добавьте первую организацию и выберите оператора ЭДО.'} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-sm">
                  <thead><tr className="border-b border-border bg-muted/40 text-left text-[11px] uppercase tracking-wider text-muted-foreground"><th className="px-5 py-3 font-semibold">Организация</th><th className="px-5 py-3 font-semibold">ИНН</th><th className="px-5 py-3 font-semibold">Оператор ЭДО</th><th className="px-5 py-3 font-semibold">Состояние</th><th className="px-5 py-3 text-right font-semibold">Управление</th></tr></thead>
                  <tbody>
                    {filteredCompanies.map((company) => {
                      const operator = operators.find((item) => item.id === Number(company.edoOperatorId));
                      return (
                        <tr key={company.id} className="border-b border-border/60 last:border-0 hover:bg-muted/30">
                          <td className="px-5 py-4"><div className="flex items-center gap-3"><span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-xs font-extrabold text-primary">{company.name.slice(0, 2).toUpperCase()}</span><div className="min-w-0"><p className="truncate font-bold text-foreground">{company.name}</p><p className="text-xs text-muted-foreground">ID #{company.id}</p></div></div></td>
                          <td className="px-5 py-4 font-mono text-xs text-foreground">{company.inn || 'Не указан'}</td>
                          <td className="px-5 py-4"><OperatorBadge operator={operator} fallback={company.edoOperatorName} /></td>
                          <td className="px-5 py-4"><span className={cn('inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-bold', company.edoOperatorId ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground')}><span className={cn('h-1.5 w-1.5 rounded-full', company.edoOperatorId ? 'bg-success' : 'bg-muted-foreground')} />{company.edoOperatorId ? 'Подключена' : 'Не подключена'}</span></td>
                          <td className="px-5 py-4 text-right"><Button size="sm" variant="outline" onClick={() => openOperatorEditor(company)}><Pencil className="h-3.5 w-3.5" /> {company.edoOperatorId ? 'Изменить' : 'Подключить'}</Button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

        </>
      )}

      <Modal open={createOpen} onClose={resetCreate} title="Добавить организацию в ЭПЛ" maxWidth="max-w-lg" footer={<><Button variant="ghost" onClick={resetCreate} disabled={submitting}>Отмена</Button><Button onClick={handleCreate} disabled={submitting || !createName.trim() || !/^\d{10}(\d{2})?$/.test(createInn) || !createOperatorId}>{submitting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}{submitting ? 'Добавляем...' : 'Добавить и подключить'}</Button></>}>
        <div className="space-y-4">
          <div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-foreground">Компания будет создана в контуре <strong>{selected?.tenantName}</strong>. История других организаций не изменится.</div>
          <Input label="Название организации *" value={createName} maxLength={50} onChange={(event) => setCreateName(event.target.value)} placeholder="ООО «Перевозчик»" />
          <Input label="ИНН *" value={createInn} maxLength={12} inputMode="numeric" onChange={(event) => setCreateInn(event.target.value.replace(/\D/g, ''))} placeholder="10 или 12 цифр" />
          <div className="space-y-1.5"><label htmlFor="create-edo-operator" className="text-sm font-semibold text-foreground">Оператор ЭДО *</label><select id="create-edo-operator" value={createOperatorId || ''} onChange={(event) => setCreateOperatorId(Number(event.target.value))} className="h-11 w-full cursor-pointer rounded-lg border border-input bg-background px-3 text-sm text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20">{operators?.map((operator) => <option key={operator.id} value={operator.id}>{operatorDisplayName(operator)}</option>)}</select></div>
        </div>
      </Modal>

      <Modal open={Boolean(editingCompany)} onClose={() => setEditingCompany(undefined)} title={`Оператор ЭДО · ${editingCompany?.name || ''}`} maxWidth="max-w-lg" footer={<><Button variant="ghost" onClick={() => setEditingCompany(undefined)} disabled={submitting}>Отмена</Button><Button onClick={handleOperatorUpdate} disabled={submitting || operatorDraft === String(editingCompany?.edoOperatorId || '')}>{submitting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}{submitting ? 'Сохраняем...' : 'Сохранить'}</Button></>}>
        <div className="space-y-4">
          <div><p className="text-sm font-bold text-foreground">{editingCompany?.name}</p><p className="mt-1 font-mono text-xs text-muted-foreground">ИНН {editingCompany?.inn || 'не указан'}</p></div>
          <div className="space-y-1.5"><label htmlFor="edit-edo-operator" className="text-sm font-semibold text-foreground">Оператор ЭДО</label><select id="edit-edo-operator" value={operatorDraft} onChange={(event) => setOperatorDraft(event.target.value)} className="h-11 w-full cursor-pointer rounded-lg border border-input bg-background px-3 text-sm text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"><option value="">Не подключать к ЭПЛ</option>{operators?.map((operator) => <option key={operator.id} value={operator.id}>{operatorDisplayName(operator)}</option>)}</select></div>
          {!operatorDraft && editingCompany?.edoOperatorId && <div className="flex items-start gap-3 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-foreground"><TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" />Будет удалена только связь с оператором. Компания, водители, осмотры и история останутся в базе.</div>}
        </div>
      </Modal>
    </AppLayout>
  );
};

const Metric = ({ label, value, icon: Icon, tone, className }: { label: string; value: number; icon: typeof Building2; tone: 'primary' | 'success' | 'neutral'; className?: string }) => (
  <div className={cn('flex items-center gap-3 rounded-xl border border-border bg-background p-3', className)}><span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', tone === 'primary' && 'bg-primary/15 text-primary', tone === 'success' && 'bg-success/15 text-success', tone === 'neutral' && 'bg-muted text-muted-foreground')}><Icon className="h-4 w-4" /></span><div><p className="text-xl font-extrabold text-foreground">{value}</p><p className="text-xs font-medium text-muted-foreground">{label}</p></div></div>
);

const FilterButton = ({ active, onClick, label, count }: { active: boolean; onClick: () => void; label: string; count: number }) => (
  <button type="button" aria-pressed={active} onClick={onClick} className={cn('min-h-10 cursor-pointer rounded-lg border px-3 py-2 text-sm font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30', active ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-foreground hover:bg-muted')}>{label} <span className={cn('ml-1 rounded px-1.5 py-0.5 text-xs', active ? 'bg-white/15' : 'bg-muted text-muted-foreground')}>{count}</span></button>
);

const OperatorBadge = ({ operator, fallback }: { operator?: EdoOperatorDto; fallback?: string }) => operator || fallback ? (
  <span className="inline-flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-2.5 py-1.5 text-xs font-bold text-foreground"><FileSignature className="h-3.5 w-3.5 text-primary" />{operatorDisplayName(operator, fallback)}</span>
) : <span className="text-sm text-muted-foreground">Не назначен</span>;

const Alert = ({ tone, icon: Icon, children }: { tone: 'error' | 'success'; icon: typeof Check; children: ReactNode }) => (
  <div role={tone === 'error' ? 'alert' : 'status'} className={cn('mb-4 flex items-start gap-3 rounded-xl border px-4 py-3 text-sm font-medium', tone === 'error' ? 'border-destructive/30 bg-destructive/10 text-destructive' : 'border-success/30 bg-success/10 text-success')}><Icon className="mt-0.5 h-4 w-4 shrink-0" />{children}</div>
);
